import '@/metrics/launchAssociateTag'
import { initializeConnectedObserver } from '@amazon/vinyl-tsx'
import { toastDebugError } from '@/debug/debugErrorToast'
import '../styles/index.scss'
import { checkInviteGate } from '@/auth/inviteGate'
import { InviteRequiredWall } from '@/auth/InviteRequiredWall'
import { initializePlaybackUiBindings } from '@/playback/playbackUiBinding'
import { initializeDynamicMessagingLazy } from '@/messaging/dm/dmBootstrap'
import { initializeMediaSessionBindings } from '@/transport/mediaSessionBinding'
import { setI18nConfig } from '@amzn/bf-i18n'
import { installPageLanguage } from '@/displayLanguage/pageLanguage'
import {
    initializeDisplayLocale,
    installDisplayLanguageSync,
} from '@/displayLanguage/displayLanguageService'
import { cblAuthConfig, stratusConfig } from '@amzn/vinyl-amzn/cbl'
import { getApiHost, proxiedHost } from '@/service/useLiveHost'
import { createAppView } from '@/AppView'
import { hideMainLoader } from '@/hideMainLoader'
import { loadDeviceInfo } from '@/device/device'
import { initializeOfflineCache } from '@/offline/initializeOfflineCache'
import { initializeSpatialKeyBindings } from '@/tv/initializeSpatialKeyBindings'
import { initializeOnScreenKeyboard } from '@/tv/initializeOnScreenKeyboard'
import { initializeExternalLinkHandoff } from '@/tv/initializeExternalLinkHandoff'
import { scrollIntoViewOnFocus } from '@/util/domUtil'
import { initializeDeviceStyling } from '@/device/initializeDeviceStyling'
import {
    createDisposer,
    getUserAgentInfo,
    logDebug,
    logInfo,
    noop,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { ctx } from '@/util/context'
import { getDeviceConfig, isInstalledDesktop, isTv } from '@/device/deviceConfig'
import { doWhenIdle, IdlePriority } from '@/util/idleQueue'
import { nextTask, yieldTask } from '@/util/timeSlice'
import { pruneOptimisticUpdates } from '@amzn/bf-gql'
import { setTrackTileMenuOpener } from '@/browse/trackTileMenu'
import { registerAddToPlaylistOpener } from '@/components/addToPlaylistOpener'
import { reapplyPersistedTrackLikes } from '@/track/trackLikeOverlay'
import { reapplyPersistedAlbumLikes } from '@/album/albumLikeOverlay'
import { reapplyPersistedArtistFollows } from '@/service/artistFollowOverlay'
import { createErrorHandler, initializeGlobalErrorHandling, onError } from '@/errorHandler'
import { setLastError } from '@/core/lastError'
import { initializeRemoteErrorReporting } from '@/core/remoteErrorReporting'
import { errorProbeEnabled, installErrorProbe } from '@/debug/errorProbe'
import {
    fatalSetupFailure$,
    installFatalSetupFailurePage,
    startCoreService,
} from '@/error/fatalSetupFailure'
import { debug } from '@/core/debug'
import { initializeRouter, registerRoutes } from '@/routes'
import { ALL_PAGES } from '@/pages/allPages'
import { preloadRoute } from '@/router/router'
import { createAppDependencies } from '@/appDependencies'
import { initializeLogging } from '@/core/initializeLogging'
import { bullfrogVersion } from '@/version'
import { reloadApplication, setReloadApplicationHandler } from '@/core/reloadApplication'
import { captureInteractionInput } from '@/metrics/interactionInput'
import { installLazily } from '@/core/installLazily'
import { installGlobalKeyboardShortcutsLazy } from '@/transport/installGlobalKeyboardShortcutsLazy'
import { registerTransportSkipEffects } from '@/transport/skipGate'
import { IS_TAURI } from '@/util/platform'
import { installAppLinksLazy } from '@/appLinks/installAppLinksLazy'
import { initializeTauri } from '@/service/initializeTauri'
import {
    downloadsSupported,
    installNativePlayback,
    nativeOutputDevices,
    nativePlaybackInstalled,
    redirectOfflineLaunch,
} from '@/downloads/nativePlayback'
import { webAudioOutputSupported } from '@/playback/webAudioOutput'
import { installPlaySearchBridgeLazy } from '@/voice/playSearchBridge'
import { applyTheme, resolveBootTheme, watchSystemTheme } from '@/blackbolt/settings/theme'
import { registerAgentWarmer } from '@/blackbolt/agent/agentWarmup'
import { hydrateAgentProviderPref } from '@/blackbolt/agent/agentProviderPref'
import { BEDROCK_PROVIDER_ID } from '@/blackbolt/agent/agentProviderIds'
import { installExplicitLanguageFilterSyncLazy } from '@/explicitLanguageFilter/installExplicitLanguageFilterSyncLazy'
import { clearPrerenderRoot } from '@amzn/prerender-contract'
import { staticPage } from '@/staticRender'
import { startFullscreenFromQuery } from '@/fullscreen/startFullscreen'
import { openModalFromQuery } from '@/dev/openModalFromQuery'
import { installDropToPlay } from '@/localFiles/installDropToPlay'
import { checkAndShowCookieConsentModalLazy } from '@/messaging/checkAndShowCookieConsentModal'
import { preseedCacheManager } from '@/service/preseedCacheManager'
import { getAuth } from '@/auth/authRef'
import { awaitAuthSettled } from '@/auth/awaitAuthSettled'
import {
    installDesktopSignInGate,
    mountDesktopSignInScreen,
    showsDesktopSignIn,
} from '@/auth/desktopSignInGate'
import { installSignOutTeardown } from '@/teardown'
import { installAccountScope } from '@/auth/accountScope'
import { installAuthRecoveryPrompt } from '@/messaging/authRecoveryPrompt'
import { queryParam } from '@/util/queryParams'
import { initializeQueueRestoreLazy } from '@amzn/bf-playback'
import { selectPlayback } from '@/playback/playbackSelection'

stratusConfig.value = {
    host: getApiHost(),
    region: 'NA',
}
cblAuthConfig.value = {
    host: proxiedHost('/proxy/api', 'https://api.amazon.com'),
}

const { target } = ctx('app')
const errorHandler = createErrorHandler(target)

;(async () => {
    // Each `yieldTask` ends a task, so module evaluation and the boot steps are not one long task.
    await yieldTask()
    // Off the boot chunk; loads alongside the boot steps below and lands before the app mounts.
    const playbackEnvInstalled = Promise.all([
        import('@/playback/webPlaybackEnv'),
        loadDeviceInfo(),
    ]).then(([m, { deviceId }]) => m.installWebPlaybackEnv(deviceId))
    // No Tauri runtime in a headless render; skip.
    if (!staticPage) await initializeTauri()
    // Before playback exists: in the desktop app the native engine takes over audio.
    if (!staticPage) installNativePlayback()
    startCoreService('ErrorReporter', () => initializeRemoteErrorReporting(playbackEnvInstalled))
    onError(setLastError)
    if (debug) onError(toastDebugError)
    initializeGlobalErrorHandling()
    if (errorProbeEnabled()) installErrorProbe()
    startCoreService('Telemetry', initializeLogging)
    // `?v=<hash>` cache-busts the i18n bundles: their JSON ships via publicDir
    // (not fingerprinted), so a changed bundle needs a changed URL to defeat the
    // browser/CDN cache. The hash (baked by vite `define`) changes only when
    // translation content changes. The `{bundle}`/`{locale}` interpolation in
    // bf-i18n's loader leaves the query untouched; the static-prerender disk
    // resolver bypasses translationPath entirely, so this is browser-only.
    const i18nVersion = typeof __I18N_CONTENT_HASH__ !== 'undefined' ? __I18N_CONTENT_HASH__ : ''
    setI18nConfig({
        translationPath: `/i18n/{bundle}/{locale}.json${i18nVersion ? `?v=${i18nVersion}` : ''}`,
    })
    // Seed the UI locale: a saved display-language preference wins over the
    // browser locale (else nothing re-reads the choice and reload reverts it).
    initializeDisplayLocale()
    installPageLanguage()
    await loadDeviceInfo()
    initializeDeviceStyling()
    applyTheme(resolveBootTheme())

    startCoreService('IdentityStore', getAuth)

    logInfo(target, `👋 Bullfrog ${bullfrogVersion.str}`)
    logDebug(target, 'initializing', getUserAgentInfo(), getDeviceConfig())
    await yieldTask()

    // Prune stale persisted optimistic overlays once the app goes idle.
    doWhenIdle(() => pruneOptimisticUpdates(), IdlePriority.LOW).catch(errorHandler)

    // Re-arm the normalized cache from persisted track likes / album likes /
    // artist follows before the first entity query settles, so a change made
    // pre-reload out-ranks the gateway's stale echo within its window. Eager,
    // not idle-gated. Each overlay is a leaf (no mutation/toggle machinery) to
    // keep the boot chunk small.
    reapplyPersistedTrackLikes().catch(errorHandler)
    reapplyPersistedAlbumLikes().catch(errorHandler)
    reapplyPersistedArtistFollows().catch(errorHandler)

    // A render is network-free and single-shot; nothing to cache or purge.
    if (!staticPage) initializeOfflineCache()
    // Must precede the first data fetch (`reloadApplication` mounts the route below).
    await preseedCacheManager()
    initializeConnectedObserver({ events: false })
    installFatalSetupFailurePage()
    if (fatalSetupFailure$().value !== null) return
    // The desktop app shows nothing until it knows whether it is signed in, as Morpho.
    if (isInstalledDesktop()) await awaitAuthSettled()
    hideMainLoader()
    await yieldTask()
    if (!staticPage) {
        // Spatial navigation scrolls the focus into view itself, scroller by scroller.
        if (!getDeviceConfig().useSpatialNavigation) scrollIntoViewOnFocus()
        // Sign-out is decoupled from teardown: `getAuth().signOut()` ends the
        // session and drops auth, and this listener runs the full cleanup on the
        // signed-out transition (app-root lifetime, never disposed).
        installSignOutTeardown(getAuth().status)
        // Cookie-session auth means this is music.amazon.com, where Skyfire left its keys.
        if (!getAuth().usesBearer()) {
            import('@/core/evictLegacySkyfireStorage')
                .then(({ evictLegacySkyfireStorage }) => evictLegacySkyfireStorage())
                .catch(errorHandler)
        }
        installAccountScope()
        installAuthRecoveryPrompt()
        // Both act on the signed-in transition, which the lazy load catches by replay.
        import('@/messaging/profileTransferPrompt')
            .then(({ installProfileTransferPrompt }) => installProfileTransferPrompt())
            .catch(errorHandler)
        import('@/tastes/tastesTriggerController')
            .then(({ installTasteCollectionTrigger }) => installTasteCollectionTrigger())
            .catch(errorHandler)
        // E2e-only: CBL boots signed out (its controller, unlike LWA, doesn't
        // read persistence on construct), so the harness triggers the silent
        // refresh-token sign-in via this param. Inert without it.
        if (queryParam('e2eSignIn') != null) getAuth().signIn()
        // Wire the library-owned Add to Playlist modal into the neutral opener
        // seam so track/podcast/album open it without importing `@/library`.
        // The modal, its styles and its library/playlistdetail deps load on first open.
        registerAddToPlaylistOpener((target, reportClick) => {
            import('@/library/AddToPlaylistModal')
                .then(({ openAddToPlaylistModal }) => openAddToPlaylistModal(target, reportClick))
                .catch(errorHandler)
        })
        import('@/library/libraryService')
            .then(({ registerLibraryHomeFollowRefresh }) => registerLibraryHomeFollowRefresh())
            .catch(errorHandler)
        if (downloadsSupported()) {
            import('@/downloads/installDownloads')
                .then(({ installDownloads }) => installDownloads(playbackEnvInstalled))
                .catch(errorHandler)
        }
        if (
            nativePlaybackInstalled() ? nativeOutputDevices() : webAudioOutputSupported() && !isTv()
        ) {
            import('@/playback/outputDevices')
                .then(({ installOutputDevices }) => installOutputDevices())
                .catch(errorHandler)
        }
        if (IS_TAURI) {
            import('@/ota/installOtaUpdater')
                .then(({ installOtaUpdater }) => installOtaUpdater())
                .catch(errorHandler)
            import('@/territoryMove')
                .then(({ installTerritoryMove }) => installTerritoryMove())
                .catch(errorHandler)
        }
        // Inject the transport-domain skip-denial surface (tooltip + DM upsell)
        // into the playback-layer skip guard. Done once at boot so any guarded
        // gesture — including the track overflow menu's, which can't import
        // transport chrome without a biref cycle — paints the upsell on denial.
        registerTransportSkipEffects()
        // The track menu, its context menu and share modal load on the first right-click.
        setTrackTileMenuOpener((options) => {
            import('@/track/openTrackMenuAtPoint')
                .then(({ openTrackMenuAtPoint }) => openTrackMenuAtPoint(options))
                .catch(errorHandler)
        })
        // Disabled, can be added to prompt to install as a PWA
        // initializeInstallPrompt()
        // Seed the persisted chat-agent provider preference before reading
        // provider selection — the pref's async get() would otherwise race this
        // sync read and the choice wouldn't take effect until a later turn. The
        // get is a microtask over localStorage, so the await is cheap.
        await hydrateAgentProviderPref()
        // Inject the agent-connection warmer: the blackbolt tier (chat-input
        // focus, `ensureAgentConnected`) warms the selected provider through
        // this seam WITHOUT a `blackbolt → @/bedrockAgent` import (that back-edge
        // would close the biref cycle; see agentWarmup.ts). app.ts owns the
        // dynamic import — the chat provider is used later (on a turn), so its
        // registration (+ transport/idb chunk) needn't block boot. Fired only on
        // first chat-input focus, so a default (claw) boot never loads it.
        registerAgentWarmer((providerId) => {
            if (providerId === BEDROCK_PROVIDER_ID) {
                import('@/bedrockAgent')
                    .then(({ registerBedrockAgentProvider }) => registerBedrockAgentProvider())
                    .catch(errorHandler)
            }
        })
        // Boot-warm the top-level browse destinations on the idle queue.
        for (const path of ['/music', '/podcasts', '/audiobooks', '/my/library']) {
            preloadRoute(path, { idle: true })
        }
        startFullscreenFromQuery()
    }

    await playbackEnvInstalled
    const playback = await selectPlayback()
    // A playback host restores its own queue and owns the media session.
    const playsLocally = playback.mode === 'local'
    let booted = false
    const runNow = (fn: () => void): Unsubscribe => {
        fn()
        return noop
    }
    setReloadApplicationHandler(() => {
        const auth = getAuth()
        if (showsDesktopSignIn(auth)) return mountDesktopSignInScreen(auth)
        const { add, dispose } = createDisposer()
        logDebug(target, 'reload application')
        // A remount builds the view and its route in one task, so the old page never paints blank.
        const step = booted ? runNow : nextTask
        booted = true

        if (!staticPage) add(watchSystemTheme())
        const deps = add(createAppDependencies(playback)).dependencies

        // At boot, building the view, mounting it, its first route and the wiring take a task each.
        const mountAppView = createAppView({ deps })
        add(
            step(() => {
                clearPrerenderRoot()
                add(mountAppView())
                add(
                    step(() => {
                        add(initializeRouter())
                        add(registerRoutes(deps, ALL_PAGES))
                        if (!staticPage) add(nextTask(wireInteractions))
                    })
                )
            })
        )

        function wireInteractions() {
            add(captureInteractionInput())
            installPlaySearchBridgeLazy({
                cloudQueueController: deps.cloudQueueController,
                playlistController: deps.playlistController,
            })
            add(initializePlaybackUiBindings(deps))
            if (playsLocally) add(installDropToPlay(deps))
            if (IS_TAURI) add(installAppLinksLazy())
            // Recover the playing queue + now-playing track (PAUSED) on reload,
            // and persist the identifiers needed to do so as the queue changes.
            // rehydrateEntity is a best-effort by-id overlay injected lazily;
            // restore falls back to the queue node if it rejects.
            if (playsLocally) {
                add(
                    initializeQueueRestoreLazy({
                        playlistController: deps.playlistController,
                        cloudQueueController: deps.cloudQueueController,
                        playerPromise: deps.playerPromise,
                        rehydrateEntity: (entity) =>
                            import('@/track/restoreEntityRehydrate').then((m) =>
                                m.rehydrateRestoredEntity(entity)
                            ),
                    })
                )
            }
            add(initializeDynamicMessagingLazy(deps))
            if (playsLocally) add(initializeMediaSessionBindings(deps))
            add(
                installLazily(
                    import('@/transport/mediaKeyBinding'),
                    (m) => m.initializeMediaKeyBindings(deps),
                    errorHandler
                )
            )
            add(
                installGlobalKeyboardShortcutsLazy({
                    playerPromise: deps.playerPromise,
                    playlistController: deps.playlistController,
                })
            )
            add(initializeSpatialKeyBindings())
            add(
                installLazily(
                    import('@/util/longPressTooltip'),
                    (m) => m.installLongPressTooltip(),
                    errorHandler
                )
            )
            add(initializeOnScreenKeyboard())
            add(initializeExternalLinkHandoff())
            add(
                installLazily(
                    import('@/messaging/inactivityMonitor'),
                    (m) => m.initializeInactivityMonitor(deps),
                    errorHandler
                )
            )
            add(installExplicitLanguageFilterSyncLazy())
            add(installDisplayLanguageSync())
            add(checkAndShowCookieConsentModalLazy())
            openModalFromQuery()
        }

        return () => {
            logDebug(target, 'dispose')
            dispose()
        }
    })

    // Gate at app load: without a valid invite (or a prior sticky unlock) show
    // the wall instead of mounting a route. A non-expiring token in the PWA
    // start_url re-unlocks the iOS home-screen partition on its first launch.
    if (await checkInviteGate()) {
        redirectOfflineLaunch()
        reloadApplication()
        if (isInstalledDesktop()) installDesktopSignInGate(getAuth().status, reloadApplication)
    } else {
        document.body.append(InviteRequiredWall())
    }
})().catch(errorHandler)
